import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import { styled } from '@mui/system';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { MdLockClock, MdClose, MdArrowBack, MdSearch } from 'react-icons/md';
import { FaStop, FaMicrophone } from 'react-icons/fa';

import { 
  IconButton, 
  Card, 
  CardContent, 
  CardHeader, 
  Typography, 
  Button as MuiButton, 
  Box,
  TextField,
  InputAdornment
} from '@mui/material';
import { MdEdit } from 'react-icons/md';

import { moment } from '../../utils/timeUtils';
import { TextArea } from '../TextArea';
import classes from './NotesDetail.module.css';
import { Link } from 'react-router-dom';
import { stringToColor } from '../../utils/functions';
import ReactMarkdown from 'react-markdown';
import EditableSummary from '../EditableSummary';

/**
 * NotesDetailPopup component displays meeting summaries, phone call summaries, and notes in a popup.
 * Allows adding and editing notes, and resizing the popup.
 *
 * @component
 * @param {Object} props
 * @param {HTMLElement} props.anchor - The anchor element for the popup.
 * @param {string|number} props.userId - The user ID for navigation.
 * @param {Array} props.notes - The list of notes to display.
 * @param {Function} props.onAdd - Handler to add a new note.
 * @param {Function} props.onClose - Handler to close the popup.
 * @param {Function} props.onEdit - Handler to edit a note.
 * @param {Object} props.selectedNote - The currently selected note (if any).
 * @param {Array} [props.meetingSummaries=[]] - List of meeting summaries.
 * @param {Array} [props.phoneCallSummaries=[]] - List of phone call summaries.
 */
const NotesDetailPopup = ( {
  anchor,
  userId,
  notes,
  onAdd,
  onClose,
  onEdit,
  selectedNote,
  meetingSummaries = [],
  phoneCallSummaries = [],
  isInterestsPage = false,
} ) =>
{
  const open = Boolean( anchor );
  const id = open ? 'notes-detail-popup' : undefined;

  const [ message, setMessage ] = useState( selectedNote?.text || '' );
  const [ flag, setFlag ] = useState( false );
  const [ meetingSummaryCount, setMeetingSummaryCount ] = useState( 1 );
  const [ phoneCallSummaryCount, setPhoneCallSummaryCount ] = useState( 1 );

  useEffect( () =>
  {
    if ( !anchor ) setFlag( false );
  }, [ anchor ] );

  const divRef = useRef( null );

  /**
   * Handles mouse down event for resizing the popup.
   * Adds mousemove and mouseup listeners to resize the popup dynamically.
   *
   * @param {MouseEvent} e - The mouse down event.
   */
  const handleMouseDown = ( e ) =>
  {
    const div = divRef.current;
    const startX = e?.clientX;
    const startY = e?.clientY;
    const startWidth = div?.offsetWidth;
    const startHeight = div?.offsetHeight;

    const onMouseMove = ( e ) =>
    {
      const newWidth = startWidth - ( e.clientX - startX );
      const newHeight = startHeight + ( e.clientY - startY );
      div.style.width = `${newWidth}px`;
      div.style.height = `${newHeight}px`;
    };

    const onMouseUp = () =>
    {
      window.removeEventListener( 'mousemove', onMouseMove );
      window.removeEventListener( 'mouseup', onMouseUp );
    };

    window.addEventListener( 'mousemove', onMouseMove );
    window.addEventListener( 'mouseup', onMouseUp );
  };

  return (
    <ClickAwayListener
      onClickAway={() =>
      {
        if ( !flag )
        {
          setFlag( true );
        } else
        {
          setFlag( false );
          onClose();
        }
      }}
    >
      <BasePopup
        id={id}
        open={open}
        anchor={anchor}
        placement="left"
        style={{ zIndex: '100' }}
      >
        <PopupBody>
          <NotesDetailContent
            userId={userId}
            notes={notes}
            onAdd={onAdd}
            onClose={onClose}
            onEdit={onEdit}
            selectedNote={selectedNote}
            meetingSummaries={meetingSummaries}
            phoneCallSummaries={phoneCallSummaries}
            isInterestsPage={isInterestsPage}
          />
        </PopupBody>
      </BasePopup>
    </ClickAwayListener>
  );
};

// Modal-friendly version (for Conversation)
export const NotesDetailModal = ( props ) =>
{
  return <NotesDetailContent {...props} />;
};

// Shared content
const NotesDetailContent = ( {
  userId,
  notes,
  onAdd,
  onClose,
  onEdit,
  selectedNote,
  meetingSummaries = [],
  phoneCallSummaries = [],
  isInterestsPage = false,
} ) =>
{
  const [ message, setMessage ] = useState( selectedNote?.text || '' );
  const [ meetingSummaryCount, setMeetingSummaryCount ] = useState( 1 );
  const [ phoneCallSummaryCount, setPhoneCallSummaryCount ] = useState( 1 );
  const [ searchTerm, setSearchTerm ] = useState( '' );
  const divRef = useRef( null );
  const [isListening, setIsListening] = useState(false);
  const recognition = useRef(null);
  const [error, setError] = useState('');

  const startListening = () => {
    if (recognition.current && !isListening) {
      setError('');
      recognition.current.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    if (recognition.current && isListening) {
      recognition.current.stop();
      setIsListening(false);
    }
  };

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Speech recognition not supported in this browser. Please use Chrome.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognition.current = new SpeechRecognition();
    
    recognition.current.continuous = true;
    recognition.current.interimResults = true;
    recognition.current.lang = 'en-US';
    
    // Handle results
    recognition.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + ' ';
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      
      if (finalTranscript) {
        setMessage(prev => prev + finalTranscript);
      }
    };
    
    // Handle errors
    recognition.current.onerror = (event) => {
      setError(`Error: ${event.error}`);
      setIsListening(false);
    };
    
    // Handle end of recognition
    recognition.current.onend = () => {
      setIsListening(false);
    };
    
    return () => {
      if (recognition.current) {
        recognition.current.stop();
      }
    };
  }, []);
  
  function formatDateMoment(isoString) {
	  return moment.utc(isoString).format('MM-DD-YYYY hh:mm');
	}
  const handleMouseDown = ( e ) =>
  {
    const div = divRef.current;
    const startX = e?.clientX;
    const startY = e?.clientY;
    const startWidth = div?.offsetWidth;
    const startHeight = div?.offsetHeight;
    const onMouseMove = ( e ) =>
    {
      const newWidth = startWidth - ( e.clientX - startX );
      const newHeight = startHeight + ( e.clientY - startY );
      div.style.width = `${newWidth}px`;
      div.style.height = `${newHeight}px`;
    };
    const onMouseUp = () =>
    {
      window.removeEventListener( 'mousemove', onMouseMove );
      window.removeEventListener( 'mouseup', onMouseUp );
    };
    window.addEventListener( 'mousemove', onMouseMove );
    window.addEventListener( 'mouseup', onMouseUp );
  };

  /**
   * Filter phone call summaries based on search term
   */
  const filteredPhoneCallSummaries = phoneCallSummaries.filter( summary => {
    if ( !searchTerm ) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      ( summary.caller && summary.caller.toLowerCase().includes( searchLower ) ) ||
      ( summary.callee && summary.callee.toLowerCase().includes( searchLower ) ) ||
      ( summary.zoomSummary && summary.zoomSummary.toLowerCase().includes( searchLower ) ) ||
      ( summary.gptSummary && summary.gptSummary.toLowerCase().includes( searchLower ) ) ||
      ( summary.events?.summaryReceivedAt && moment( summary.events.summaryReceivedAt ).format( 'MM-DD-YYYY hh:mm' ).toLowerCase().includes( searchLower ) ) ||
      ( summary.createdAt && moment( summary.createdAt ).format( 'MM-DD-YYYY hh:mm' ).toLowerCase().includes( searchLower ) )
    );
  } );

  const filteredMeetingSummaries = meetingSummaries.filter( summary => {
    if ( !searchTerm ) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      ( summary.direction && summary.direction.toLowerCase().includes( searchLower ) ) ||
      ( summary.zoomSummary && summary.zoomSummary.toLowerCase().includes( searchLower ) ) ||
      ( summary.gptSummary && summary.gptSummary.toLowerCase().includes( searchLower ) ) ||
      ( summary.start_time && moment( summary.start_time ).format( 'MM-DD-YYYY hh:mm' ).toLowerCase().includes( searchLower ) )
    );
  } );

  const totalFilteredItems = filteredMeetingSummaries.length + filteredPhoneCallSummaries.length;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <MdClose className={classes.closeBtn} onClick={onClose} />
      </div>
      <div ref={divRef} className={classes.content} style={{Position:'unset !important'}}>

      	{/* Search Box for Phone Call Summaries */}
            <Box sx={{ mb: 2, p: 1, background: '#f8f9fa', borderRadius: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={isInterestsPage? "Search": "Search phone calls by caller, callee, summary, or date..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MdSearch />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                        sx={{ fontSize: '18px' }}
                      >
                        <MdClose />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    backgroundColor: 'white',
                  }
                }}
              />
              {searchTerm && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  Showing {totalFilteredItems} of {meetingSummaries.length + phoneCallSummaries.length} summaries matching "{searchTerm}"
                </Typography>
              )}
            </Box>

        {/* Meeting Summaries Section */}
        {Array.isArray( meetingSummaries ) && meetingSummaries.length > 0 && (
          <Box mb={2}>
          	{filteredMeetingSummaries.length > 0 ? (
          		<>
            {filteredMeetingSummaries
              .sort((a, b) => {
				  const dateA = a.events?.meetingStartedAt ? new Date(a.events.meetingStartedAt) : new Date(0);
				  const dateB = b.events?.meetingStartedAt ? new Date(b.events.meetingStartedAt) : new Date(0);
				  return dateB - dateA; // descending: newest first
				})
              .slice( 0, meetingSummaryCount )
              .map( ( summary, idx ) => (
                <Card key={summary._id || idx} sx={{ mb: 2, background: '#f5f6fa', border: '1px solid #d1d5db' }}>
                  <CardHeader
                    title={<Typography variant="h6" color="primary">Meeting Summary</Typography>}
                    sx={{ pb: 0 }}
                  />
                  <CardContent>
                    <Typography variant="subtitle2"><b>Topic:</b> {summary.direction}</Typography>
                    <Typography variant="subtitle2"><b>Date:</b> {formatDateMoment(summary.events.meetingStartedAt)}</Typography>
                    {( summary.zoomSummary || summary.gptSummary ) && (
                      <EditableSummary summary={summary} />
                    )}
                    {summary.recording_url && (
                      <Box mt={1}>
                        <a href={summary.recording_url} target="_blank" rel="noopener noreferrer">View Recording</a>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) )}
            {meetingSummaryCount < meetingSummaries.length && (
              <Box display="flex" justifyContent="center" mb={2}>
                <MuiButton variant="outlined" onClick={() => setMeetingSummaryCount( c => Math.min( c + 3, meetingSummaries.length ) )}>
                  Load More Meeting Summaries
                </MuiButton>
              </Box>
            )}
          	</>
            ) : searchTerm && (
              <Box sx={{ textAlign: 'center', p: 3, background: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No meeting summaries found matching "{searchTerm}"
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        {/* Phone Call Summaries Section */}
        {Array.isArray( phoneCallSummaries ) && phoneCallSummaries.length > 0 && (
          <Box mb={2}>
            

            {filteredPhoneCallSummaries.length > 0 ? (
              <>
                {filteredPhoneCallSummaries
                  .sort( ( a, b ) => new Date( b.events?.summaryReceivedAt || b.createdAt ) - new Date( a.events?.summaryReceivedAt || a.createdAt ) )
                  .slice( 0, phoneCallSummaryCount )
                  .map( ( summary, idx ) => (
                    <Card key={summary._id || idx} sx={{ mb: 2, background: '#e6f7f7', border: '1px solid #7ed6df' }}>
                      <CardHeader
                        avatar={<span role="img" aria-label="phone" style={{ fontSize: 22 }}>📞</span>}
                        title={<Typography variant="h6" color="primary">Phone Call Summary</Typography>}
                        sx={{ pb: 0 }}
                      />
                      <CardContent>
                        <Typography variant="subtitle2"><b>Caller:</b> {summary.caller || '-'}</Typography>
                        <Typography variant="subtitle2"><b>Callee:</b> {summary.callee || '-'}</Typography>
                        <Typography variant="subtitle2"><b>Date:</b> {summary.events?.summaryReceivedAt
                          ? moment( summary.events.summaryReceivedAt ).format( 'MM-DD-YYYY hh:mm' )
                          : summary.createdAt
                            ? moment( summary.createdAt ).format( 'MM-DD-YYYY hh:mm' )
                            : '-'}</Typography>
                        {( summary.zoomSummary || summary.gptSummary ) && (
                          <EditableSummary summary={summary} />
                        )}
                      </CardContent>
                    </Card>
                  ) )}
                {phoneCallSummaryCount < filteredPhoneCallSummaries.length && (
                  <Box display="flex" justifyContent="center" mb={2}>
                    <MuiButton variant="outlined" onClick={() => setPhoneCallSummaryCount( c => Math.min( c + 3, filteredPhoneCallSummaries.length ) )}>
                      Load More Phone Call Summaries
                    </MuiButton>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', p: 3, background: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No phone call summaries found matching "{searchTerm}"
                </Typography>
                <MuiButton 
                  variant="text" 
                  size="small" 
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 1 }}
                >
                  Clear search
                </MuiButton>
              </Box>
            )}
          </Box>
        )}

        {/* Notes Section */}
        {notes &&
          notes.map( ( note, index ) => (
            <div className={classes.card} key={`${index}`}>
              <div
                className="d-flex justify-content-between"
                width="100%"
              >
                <div
                  className={classes.cardHeader}
                  style={{ color: stringToColor( `${note?.writer}` ) }}
                >
                  {`${note?.writer}`}
                </div>
                <IconButton
                  IconButton
                  onClick={() =>
                  {
                    onEdit && onEdit( note );
                    setMessage( note?.text );
                  }}
                  sx={{ height: 30, width: 30 }}
                  size="small"
                >
                  <MdEdit fontSize="medium" />
                </IconButton>
              </div>
              <div className={classes.cardContent}>{note?.text}</div>
              <div className={classes.cardFooter}>
                <div className={classes.timeContainer}>
                  <MdLockClock />{' '}
                  {moment( note?.date ).format( 'MM-DD-YYYY hh:mm' )}
                </div>
                <div className={classes.companyContainer}>
                  {note?.listing ? note?.listing?.companyName : ''}
                </div>
              </div>
            </div>
          ) )}

        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            bottom: "27px",
            left: '5px',
            width: '20px',
            height: '20px',
            cursor: 'nesw-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1px',
            flexDirection: 'column',
            transform: 'rotate(45deg)',
          }}
        >
          <div
            style={{ border: '6px solid transparent', width: '20px' }}
          />
          <div
            style={{ borderBottom: '1px solid black', width: '14px' }}
          />
          <div
            style={{ borderBottom: '1px solid black', width: '10px' }}
          />
        </div>
      </div>
      <div>
        <TextArea
          autoFocus={true}
          rows={1}
          value={message ? message[0].toUpperCase() + message.slice(1) : ''}
          placeholder={'New Note Here'}
          setter={setMessage}
          customStyle={{ minHeight: '50px', maxHeight: '150px' }}
        />
      </div>
      <div>
        <IconButton
          IconButton
          onClick={startListening}
          sx={{ height: 30, width: 30, color: '#1976d2' }}
          disabled={isListening || !!error}
          size="small"
        >
          <FaMicrophone fontSize="medium" />
        </IconButton>
        <IconButton
          IconButton
          sx={{ height: 30, width: 30, color: '#d32f2f' }}
          size="small"
          disabled={!isListening}
          onClick={stopListening}
        >
          <FaStop fontSize="medium" />
        </IconButton>
      </div>
      <div className={classes.footer}>
        <Link
          className={[
            'ellipsis1Line emailLink',
            classes.btnContainer,
          ].join( ' ' )}
          to={`/contacts/${userId}`}
        >
          <MdArrowBack />{' '}
          <div style={{ textTransform: 'none' }}>Contacts</div>
        </Link>
        <div
          className={[
            classes.btnContainer,
            message || classes.btnDisable,
          ].join( ' ' )}
          onClick={() => message && ( onAdd && onAdd( `${message[0].toUpperCase()}${message.slice(1)}` ), setMessage( '' ) )}
        >
          {!selectedNote ? 'Add Note' : 'Update Note'}
        </div>
      </div>
    </div>
  );
};

export default NotesDetailPopup;

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const PopupBody = styled( 'div' )(
  ( { theme } ) => `
  width: max-content;
  padding: 12px 16px;
  margin: 8px;
  border-radius: 8px;
  border: 1px solid ${theme.palette.mode === 'dark' ? grey[ 700 ] : grey[ 200 ]};
  background-color: ${theme.palette.mode === 'dark' ? grey[ 900 ] : '#fff'};
  box-shadow: ${theme.palette.mode === 'dark'
      ? `0px 4px 8px rgb(0 0 0 / 0.7)`
      : `0px 4px 8px rgb(0 0 0 / 0.1)`
    };
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  z-index: 1;
`,
);

NotesDetailPopup.propTypes = {
  anchor: PropTypes.any,
  userId: PropTypes.oneOfType( [ PropTypes.string, PropTypes.number ] ),
  notes: PropTypes.arrayOf( PropTypes.shape( {
    text: PropTypes.string,
    date: PropTypes.any,
    writer: PropTypes.string,
    listing: PropTypes.any,
    _id: PropTypes.any,
    } ) ),
  onAdd: PropTypes.func,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  selectedNote: PropTypes.shape( {
    text: PropTypes.string,
    // add other fields if needed
  } ),
  meetingSummaries: PropTypes.array,
  phoneCallSummaries: PropTypes.array,
};